import React, { useState } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { Bell, Loader2, Settings2 } from "lucide-react";
import { DesktopLayout } from "@/layouts/DesktopLayout";
import type { NotificationCategory } from "@/api/notifications";
import { useNotifications } from "@/hooks/useNotifications";
import {
  MarkAllReadButton,
  NotificationFilters,
  NotificationRow,
  NotificationSettingsList,
} from "@/components/notifications/NotificationSections";
import { EmptyState } from "@/components/ui/EmptyState";

export const NotificationsPageDesktop: React.FC = () => {
  const userId = useLaunchParams().initData?.user?.id?.toString() || "";
  const [category, setCategory] = useState<NotificationCategory | null>(null);
  const { data, isLoading } = useNotifications(userId, category);
  const items = data?.items ?? [];

  return (
    <DesktopLayout
      pageTitle="Notifications"
      pageDescription="Stay updated with your price alerts, trades and important activity."
      showRightSidebar={false}
    >
      <div className="grid items-start gap-6 pb-8 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex min-w-0 flex-col gap-6">
          <NotificationFilters value={category} onChange={setCategory} />
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-white">Last Notifications</h2>
            <MarkAllReadButton userId={userId} disabled={!data?.unread} />
          </div>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="size-8 animate-spin text-accent" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="You're all caught up"
              description="Price alerts, trades, listings and account updates will show up here."
            />
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item) => (
                <NotificationRow key={item.id} item={item} userId={userId} />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <section className="glass flex flex-col gap-5 !rounded-2xl p-4">
            <div>
              <p className="flex items-center gap-2 text-base font-semibold text-white">
                <Settings2 className="size-5 text-[#2dcc89]" />
                Notification Settings
              </p>
              <p className="text-xs text-[#a3a3a3]">Choose which notifications you want to receive.</p>
            </div>
            <NotificationSettingsList userId={userId} kind="types" />
          </section>
          <section className="glass flex flex-col gap-5 !rounded-2xl p-4">
            <div>
              <p className="flex items-center gap-2 text-base font-semibold text-white">
                <Bell className="size-5 text-[#2dcc89]" />
                Notification Channels
              </p>
              <p className="text-xs text-[#a3a3a3]">Choose how you want to receive notifications.</p>
            </div>
            <NotificationSettingsList userId={userId} kind="channels" />
          </section>
        </div>
      </div>
    </DesktopLayout>
  );
};
